'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { JSX } from 'react';
import { OutcomeDot } from '@/components/shared/outcome-indicator';
import { SelectField, type SelectFieldItem } from '@/components/ui/select-field';
import { outcomeHueToStepVar, useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { cn } from '@/lib/ui/utils';

// ── Types ────────────────────────────────────────────────────────────────────

interface OutcomeOption {
  index: number;
  label: string;
  /** Optional outcome image URL from backend. */
  imageUrl?: string | undefined;
  /** NSFW moderation flag for the outcome image (spec 071). */
  imageNsfw?: boolean | undefined;
}
interface OutcomePillsProps {
  /** Parent market / proposal pubkey — seeds the outcome indicator's pattern lib. */
  marketPubkey: string;
  outcomes: OutcomeOption[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  /** Optional prefix label shown inside the button (e.g. "From", "To"). */
  prefixLabel?: string;
  /** When true, the trigger button renders without its own border. */
  borderless?: boolean;
  /** Pre-formatted balance strings keyed by outcome index, shown in dropdown rows. */
  outcomeBalances?: Readonly<Record<number, string>>;
  /** Show outcome images in indicators. Default: true. Set false for trade modal. */
  showImages?: boolean;
  /** Extra classes for the underlying Select trigger (e.g. surface bg override). */
  triggerClassName?: string;
}

// ── Component ────────────────────────────────────────────────────────────────

/**
 * Outcome selector — built on Radix Select so it works inside SilkSheet on
 * mobile (Radix Popover gets its touch events swallowed by Silk's gesture
 * handlers; Radix Select portals to body and escapes that scope).
 *
 * Hidden when there are no outcomes; renders a static label when only one.
 */
export function OutcomePills(t0) {
  const $ = _c(59);
  const {
    outcomes,
    selectedIndex,
    onSelect,
    prefixLabel,
    borderless: t1,
    outcomeBalances,
    showImages: t2,
    triggerClassName
  } = t0;
  const borderless = t1 === undefined ? false : t1;
  t2 === undefined ? true : t2;
  const t = useTranslations("trade");
  let realOutcomes;
  let t3;
  let t4;
  if ($[0] !== outcomes) {
    realOutcomes = outcomes.filter(_temp);
    t3 = useOutcomeColor;
    t4 = realOutcomes.map(_temp2);
    $[0] = outcomes;
    $[1] = realOutcomes;
    $[2] = t3;
    $[3] = t4;
  } else {
    realOutcomes = $[1];
    t3 = $[2];
    t4 = $[3];
  }
  const colorClasses = t3(t4);
  let colorByIndex;
  if ($[4] !== colorClasses || $[5] !== realOutcomes) {
    colorByIndex = {};
    for (let i = 0; i < realOutcomes.length; i++) {
      const o_1 = realOutcomes[i];
      const cls = colorClasses[i];
      if (o_1 !== undefined && cls !== undefined) {
        colorByIndex[o_1.index] = outcomeHueToStepVar(cls.hue, 400);
      }
    }
    $[4] = colorClasses;
    $[5] = realOutcomes;
    $[6] = colorByIndex;
  } else {
    colorByIndex = $[6];
  }
  if (outcomes.length === 0) {
    return null;
  }
  const hasMultiple = outcomes.length > 1;
  let t5;
  if ($[7] !== outcomes || $[8] !== selectedIndex) {
    let t6;
    if ($[10] !== selectedIndex) {
      t6 = o_2 => o_2.index === selectedIndex;
      $[10] = selectedIndex;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    t5 = outcomes.find(t6);
    $[7] = outcomes;
    $[8] = selectedIndex;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const selected = t5;
  if (!hasMultiple) {
    const isAllSelected = selected !== undefined && selected.index < 0;
    const selectedDotColor = selected !== undefined && !isAllSelected ? colorByIndex[selected.index] ?? "" : undefined;
    const selectedBalance = selected !== undefined ? outcomeBalances?.[selected.index] : undefined;
    const t6 = borderless ? "px-3 py-2" : "h-10 rounded-control border border-border-primary bg-background-secondary px-3";
    let t7;
    if ($[12] !== t6) {
      t7 = cn("flex w-full items-center justify-between gap-2 text-body-lg", t6);
      $[12] = t6;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    let t8;
    if ($[14] !== prefixLabel) {
      t8 = prefixLabel !== undefined && prefixLabel.length > 0 && <span className="shrink-0 text-caption font-normal text-foreground-secondary">{prefixLabel}</span>;
      $[14] = prefixLabel;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    let t9;
    if ($[16] !== isAllSelected || $[17] !== selectedDotColor) {
      t9 = !isAllSelected && selectedDotColor !== undefined && <OutcomeDot color={selectedDotColor} />;
      $[16] = isAllSelected;
      $[17] = selectedDotColor;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    let t10;
    if ($[19] !== selected?.label || $[20] !== t) {
      t10 = selected?.label ?? t("select_outcome");
      $[19] = selected?.label;
      $[20] = t;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    let t11;
    if ($[22] !== t10) {
      t11 = <span className="truncate">{t10}</span>;
      $[22] = t10;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    let t12;
    if ($[24] !== t11 || $[25] !== t8 || $[26] !== t9) {
      t12 = <div className="flex min-w-0 items-center gap-2 truncate">{t8}{t9}{t11}</div>;
      $[24] = t11;
      $[25] = t8;
      $[26] = t9;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    let t13;
    if ($[28] !== selectedBalance) {
      t13 = selectedBalance !== undefined && <span className="shrink-0 text-caption tabular-nums text-foreground-secondary">{selectedBalance}</span>;
      $[28] = selectedBalance;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    let t14;
    if ($[30] !== t12 || $[31] !== t13 || $[32] !== t7) {
      t14 = <div className={t7}>{t12}{t13}</div>;
      $[30] = t12;
      $[31] = t13;
      $[32] = t7;
      $[33] = t14;
    } else {
      t14 = $[33];
    }
    return t14;
  }
  let t6;
  if ($[34] !== colorByIndex || $[35] !== outcomeBalances || $[36] !== outcomes) {
    let t7;
    if ($[38] !== colorByIndex || $[39] !== outcomeBalances) {
      t7 = outcome => {
        const isAllOption = outcome.index < 0;
        const dotStyle = colorByIndex[outcome.index] ?? "";
        const balance = outcomeBalances?.[outcome.index];
        return {
          value: outcome.index.toString(),
          label: <span className="flex w-full min-w-0 items-baseline gap-2">{!isAllOption && <OutcomeDot color={dotStyle} />}<span className="truncate">{outcome.label}</span>{balance !== undefined && <span className="ml-auto shrink-0 text-caption tabular-nums text-foreground-secondary">{balance}</span>}</span>
        };
      };
      $[38] = colorByIndex;
      $[39] = outcomeBalances;
      $[40] = t7;
    } else {
      t7 = $[40];
    }
    t6 = outcomes.map(t7);
    $[34] = colorByIndex;
    $[35] = outcomeBalances;
    $[36] = outcomes;
    $[37] = t6;
  } else {
    t6 = $[37];
  }
  const items = t6;
  let t7;
  if ($[41] !== selectedIndex) {
    t7 = selectedIndex.toString();
    $[41] = selectedIndex;
    $[42] = t7;
  } else {
    t7 = $[42];
  }
  let t8;
  if ($[43] !== onSelect) {
    t8 = v => {
      onSelect(Number(v));
    };
    $[43] = onSelect;
    $[44] = t8;
  } else {
    t8 = $[44];
  }
  let t9;
  if ($[45] !== t) {
    t9 = t("select_outcome");
    $[45] = t;
    $[46] = t9;
  } else {
    t9 = $[46];
  }
  let t10;
  if ($[47] !== prefixLabel) {
    t10 = prefixLabel !== undefined && prefixLabel.length > 0 ? {
      label: prefixLabel
    } : {};
    $[47] = prefixLabel;
    $[48] = t10;
  } else {
    t10 = $[48];
  }
  const t11 = borderless && "border-0 bg-transparent shadow-none";
  let t12;
  if ($[49] !== t11 || $[50] !== triggerClassName) {
    t12 = cn(t11, triggerClassName);
    $[49] = t11;
    $[50] = triggerClassName;
    $[51] = t12;
  } else {
    t12 = $[51];
  }
  let t13;
  if ($[52] !== items || $[53] !== t10 || $[54] !== t12 || $[55] !== t7 || $[56] !== t8 || $[57] !== t9) {
    t13 = <SelectField value={t7} onValueChange={t8} items={items} placeholder={t9} {...t10} triggerClassName={t12} />;
    $[52] = items;
    $[53] = t10;
    $[54] = t12;
    $[55] = t7;
    $[56] = t8;
    $[57] = t9;
    $[58] = t13;
  } else {
    t13 = $[58];
  }
  return t13;
}
function _temp2(o_0) {
  return {
    title: o_0.label,
    index: o_0.index
  };
}
function _temp(o) {
  return o.index >= 0;
}